import React, { useState, useEffect, useRef } from 'react';
import { Check, X } from 'lucide-react';
import {
  Point, Size, coverScale, clampOffset, centeredOffset, zoomAtCenter, sourceSquare, outputSize,
} from '../../studio/cropGeometry';

const VIEW = 360;
const MAX_ZOOM = 4;

interface ImageCropModalProps {
  file: Blob;
  onCancel: () => void;
  onConfirm: (cropped: Blob) => void;
}

// A centered popup that crops a picture to a square: drag to position, slider to zoom.
const ImageCropModal: React.FC<ImageCropModalProps> = ({ file, onCancel, onConfirm }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [image, setImage] = useState<Size | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const element = useRef<HTMLImageElement | null>(null);
  const drag = useRef<{ pointer: Point; offset: Point } | null>(null);

  useEffect(() => {
    // In development React runs this effect, cleans it up, and runs it again. The first run's
    // picture then fails to load because its address was revoked, so a run that has been
    // cleaned up must not report anything, and each run starts with a clean slate.
    let cancelled = false;
    setFailed(false);
    setImage(null);

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      if (cancelled) return;
      const size = { width: img.naturalWidth, height: img.naturalHeight };
      element.current = img;
      setImage(size);
      setZoom(1);
      setOffset(centeredOffset(size, coverScale(size, VIEW), VIEW));
    };
    img.onerror = () => {
      if (!cancelled) setFailed(true);
    };
    img.src = objectUrl;
    setUrl(objectUrl);

    return () => {
      cancelled = true;
      URL.revokeObjectURL(objectUrl);
    };
  }, [file]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const scale = image ? coverScale(image, VIEW) * zoom : 1;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!image) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointer: { x: event.clientX, y: event.clientY }, offset };
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!image || !drag.current) return;
    const start = drag.current;
    setOffset(clampOffset(
      { x: start.offset.x + event.clientX - start.pointer.x, y: start.offset.y + event.clientY - start.pointer.y },
      image,
      scale,
      VIEW
    ));
  };

  const handlePointerUp = () => {
    drag.current = null;
  };

  const handleZoom = (nextZoom: number) => {
    if (!image) return;
    const nextScale = coverScale(image, VIEW) * nextZoom;
    setOffset(zoomAtCenter(offset, scale, nextScale, image, VIEW));
    setZoom(nextZoom);
  };

  const confirm = () => {
    if (!image || !element.current) return;
    setSaving(true);
    const source = sourceSquare(offset, scale, VIEW);
    const size = outputSize(source.size);
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const context = canvas.getContext('2d')!;
    context.fillStyle = 'white';
    context.fillRect(0, 0, size, size);
    context.drawImage(element.current, source.x, source.y, source.size, source.size, 0, 0, size, size);
    canvas.toBlob(blob => {
      setSaving(false);
      if (blob) onConfirm(blob); else setFailed(true);
    }, 'image/jpeg', 0.92);
  };

  return (
    <div className="crop-overlay" onClick={onCancel}>
      <div className="crop-modal" onClick={(event) => event.stopPropagation()} role="dialog" aria-label="Crop picture">
        <h3>Crop to a square</h3>

        {failed ? (
          <p className="studio-error">This file could not be read as an image.</p>
        ) : (
          <>
            <div
              className="crop-view"
              style={{ width: VIEW, height: VIEW }}
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
            >
              {url && image && (
                <img
                  src={url}
                  alt=""
                  draggable={false}
                  style={{
                    left: offset.x,
                    top: offset.y,
                    width: image.width * scale,
                    height: image.height * scale,
                  }}
                />
              )}
            </div>

            <label className="crop-zoom">
              Zoom
              <input
                type="range"
                min={1}
                max={MAX_ZOOM}
                step={0.01}
                value={zoom}
                disabled={!image}
                onChange={(event) => handleZoom(Number(event.target.value))}
              />
            </label>
            <p className="studio-hint">Drag the picture to position it. It is saved as a square, compressed to 200 KB or less.</p>
          </>
        )}

        <div className="crop-actions">
          <button className="btn btn-secondary" onClick={onCancel} disabled={saving}>
            <X size={16} /> Cancel
          </button>
          {!failed && (
            <button className="btn btn-primary" onClick={confirm} disabled={!image || saving}>
              <Check size={16} /> Use this crop
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImageCropModal;
